/**
 * MaestroPartidas
 * Página para gestionar el maestro de Partidas
 * CRUD completo para crear, editar, listar y eliminar partidas
 * Las partidas se muestran de forma independiente sin agrupar por área
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiArrowLeft, FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import presupuestoHierarchyService from '../../services/presupuestoHierarchyService';
import CrearPartidaModal from '../../components/maestros/CrearPartidaModal';
import EditarPartidaModal from '../../components/maestros/EditarPartidaModal';
import Loading from '../../components/Loading';
import '../../styles/maestros.css';

const MaestroPartidas = () => {
  const navigate = useNavigate();
  const [partidas, setPartidas] = useState([]);
  const [filteredPartidas, setFilteredPartidas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [crearModalOpen, setCrearModalOpen] = useState(false);
  const [editarModalOpen, setEditarModalOpen] = useState(false);
  const [partidaSeleccionada, setPartidaSeleccionada] = useState(null);

  useEffect(() => {
    cargarPartidas();
  }, []);

  useEffect(() => {
    // Filtrar partidas según búsqueda
    if (searchTerm.trim() === '') {
      setFilteredPartidas(partidas);
    } else {
      const filtered = partidas.filter(partida =>
        partida.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (partida.descripcion && partida.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPartidas(filtered);
    }
  }, [searchTerm, partidas]);

  const cargarPartidas = async () => {
    setLoading(true);
    try {
      const response = await presupuestoHierarchyService.getMaestroPartidas();
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setPartidas(data);
      setFilteredPartidas(data);
    } catch (error) {
      console.error('Error al cargar partidas:', error);
      toast.error('Error al cargar las partidas');
    } finally {
      setLoading(false);
    }
  };


  const handleEliminarPartida = async (partidaId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta partida?')) {
      return;
    }

    try {
      await presupuestoHierarchyService.deleteMaestroPartida(partidaId);
      toast.success('Partida eliminada exitosamente');
      cargarPartidas();
    } catch (error) {
      console.error('Error al eliminar partida:', error);
      toast.error('Error al eliminar la partida');
    }
  };

  const handleCrearPartida = async (datosPartida) => {
    try {
      await presupuestoHierarchyService.createMaestroPartida(datosPartida);
      toast.success('Partida creada exitosamente');
      setCrearModalOpen(false);
      cargarPartidas();
    } catch (error) {
      console.error('Error al crear partida:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la partida');
    }
  };

  const handleEditarPartida = async (datosPartida) => {
    try {
      await presupuestoHierarchyService.updateMaestroPartida(partidaSeleccionada.id, datosPartida);
      toast.success('Partida actualizada exitosamente');
      setEditarModalOpen(false);
      setPartidaSeleccionada(null);
      cargarPartidas();
    } catch (error) {
      console.error('Error al actualizar partida:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar la partida');
    }
  };


  if (loading) {
    return <Loading />;
  }

  return (
    <div className="maestro-partidas">
      <div className="header">
        <div>
          <h1>Maestro de Partidas</h1>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.95rem' }}>
            Total de partidas: <strong>{partidas.length}</strong>
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setCrearModalOpen(true)}
        >
          <FiPlus /> Nueva Partida
        </button>
      </div>

      {/* Buscador */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <FiSearch style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
          <input
            type="text"
            placeholder="Buscar partidas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              paddingLeft: '2.5rem',
              paddingRight: '1rem',
              padding: '0.75rem 1rem 0.75rem 2.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.95rem',
              fontFamily: 'inherit'
            }}
          />
        </div>
      </div>

      {partidas.length === 0 ? (
        <div className="empty-state">
          <p>No hay partidas creadas aún</p>
          <button
            className="btn btn-primary"
            onClick={() => setCrearModalOpen(true)}
          >
            Crear Primera Partida
          </button>
        </div>
      ) : filteredPartidas.length === 0 ? (
        <div className="empty-state">
          <p>No hay partidas que coincidan con la búsqueda</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{
                background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                color: 'white'
              }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Nombre</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Descripción</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Unidad</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Estado</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredPartidas.map((partida, index) => (
                <tr key={partida.id} style={{
                  borderBottom: '1px solid #e5e7eb',
                  transition: 'background-color 0.2s',
                  backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f9fafb'}
                >
                  <td style={{ padding: '1rem', fontWeight: '500' }}>{partida.nombre}</td>
                  <td style={{ padding: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                    {partida.descripcion || '-'}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    {partida.unidad ? (
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}>
                        {partida.unidad}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      backgroundColor: partida.status === 'activo' ? '#dcfce7' : '#fee2e2',
                      color: partida.status === 'activo' ? '#15803d' : '#991b1b',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {partida.status}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setPartidaSeleccionada(partida);
                          setEditarModalOpen(true);
                        }}
                        title="Editar partida"
                        style={{
                          padding: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleEliminarPartida(partida.id)}
                        title="Eliminar partida"
                        style={{
                          padding: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CrearPartidaModal
        isOpen={crearModalOpen}
        onClose={() => setCrearModalOpen(false)}
        onSubmit={handleCrearPartida}
      />

      {partidaSeleccionada && (
        <EditarPartidaModal
          isOpen={editarModalOpen}
          onClose={() => {
            setEditarModalOpen(false);
            setPartidaSeleccionada(null);
          }}
          partida={partidaSeleccionada}
          onSubmit={handleEditarPartida}
        />
      )}
    </div>
  );
};

export default MaestroPartidas;
